import { type AppDispatch, type RootState } from "@/store/store";
import { ScanObject } from "@/typed/DentalCase";
import { Issue } from "@/typed/Issue";
import { ModelLabel } from "@/typed/ModelTypes";
import { generateModelThumbnail, parseDataToGeometry } from "@/utils/generateModelThumbnail";
import { isBasedModel } from "@/utils/geometry";
import { invalidateIndexedDBData } from "@/utils/indexeddb";
import {
  addNewUploadToIndexedDB,
  classifyScanInIndexedDB,
  clearUploadsStore,
  deleteUploadFromStore,
  getUploadScansFromIndexedDB,
  NewUploadObjectIndexedDB,
  removeAndAddScansFromIndexedDB,
  setCombinedThumbnailFromIndexedDB,
  setThumbnailFromIndexedDB
} from "@/utils/uploadsCache";
import { createAsyncThunk, createListenerMiddleware, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

const sortScans = (scans: ScanObject[]): ScanObject[] => {
  // Convert mapping to array with keys
  const entries = scans.map((scan, index) => ({
    key: index,
    ...scan
  }));

  // Get most recent upper & lower
  const upperEntries = entries.filter((item) => item.label === "upper");
  const mostRecentUpper = upperEntries.length
    ? upperEntries.reduce((prev, curr) => ((curr.lastModified ?? 0) > (prev.lastModified ?? 0) ? curr : prev))
    : null;

  const lowerEntries = entries.filter((item) => item.label === "lower");
  const mostRecentLower = lowerEntries.length
    ? lowerEntries.reduce((prev, curr) => ((curr.lastModified ?? 0) > (prev.lastModified ?? 0) ? curr : prev))
    : null;

  // Remove the most recent upper and lower & sort remaining scans.
  const remaining = entries.filter(
    (item) => (mostRecentUpper ? item.key !== mostRecentUpper.key : true) && (mostRecentLower ? item.key !== mostRecentLower.key : true)
  );

  remaining.sort((a, b) => (b.lastModified ?? 0) - (a.lastModified ?? 0));

  // Build the final sorted array.
  const sortedMapping: Array<{ key: number } & ScanMappingValue> = [];
  if (mostRecentUpper) {
    sortedMapping.push(mostRecentUpper);
  }
  if (mostRecentLower) {
    sortedMapping.push(mostRecentLower);
  }
  sortedMapping.push(...remaining);

  return sortedMapping.map((entry) => scans[entry.key]);
};

interface CasesState {
  numCases: number;
  highestUploadId: number;
  cases: CasesMapping;
  scans: ScanMapping;
  selected: { [uploadId: number]: string[] };
  draggingOver?: { id: number; eventSeen: number; overScan: boolean };
  isUploading: boolean;
  noScansDetectedError: boolean;
  emptyFolderError: boolean;
  isInspecting: boolean;
  inspectingCaseIndex: number | null;
  inspectingScanId: string | null;
  currentProcessingBatchSize: number;
  areUploadsSorted: boolean;
}

const caseUploadsListener = createListenerMiddleware();
const startCaseUploadListening = caseUploadsListener.startListening.withTypes<RootState, AppDispatch>();

// Begins with no listing selected
const initialState: CasesState = {
  numCases: 0,
  highestUploadId: -1,
  cases: {},
  scans: {},
  selected: {},
  isUploading: false,
  noScansDetectedError: false,
  emptyFolderError: false,
  isInspecting: false,
  inspectingCaseIndex: null,
  inspectingScanId: null,
  currentProcessingBatchSize: 0,
  areUploadsSorted: false
} satisfies CasesState;

const casesSlice = createSlice({
  name: "cases",
  initialState: initialState,
  reducers: {
    setHighestUploadId(state, action: PayloadAction<number>) {
      state.highestUploadId = action.payload;
    },
    incrementUploadId(state) {
      state.highestUploadId += 1;
    },
    setCaseName(state, action: PayloadAction<{ uploadId: number; newName: string }>) {
      state.cases[action.payload.uploadId].name = action.payload.newName;
    },
    setExpandCaseUpload(state, action: PayloadAction<{ uploadId: number; expand: boolean }>) {
      state.cases[action.payload.uploadId].expanded = action.payload.expand;
    },
    setAreCaseUploadThumbnailsAvailable(state, action: PayloadAction<{ uploadId: number; areAvailable: boolean }>) {
      state.cases[action.payload.uploadId].areThumbnailsAvailable = action.payload.areAvailable;
    },
    setCaseUploadThumbnailsAreLoading(state, action: PayloadAction<{ uploadId: number; loading: boolean }>) {
      state.cases[action.payload.uploadId].areThumbnailsLoading = action.payload.loading;
    },
    setInspecting(state, action: PayloadAction<{ caseIndex: number | null; scanId: string | null }>) {
      state.inspectingCaseIndex = action.payload.caseIndex;
      state.inspectingScanId = action.payload.scanId;
    },
    setIsInspecting(state, action: PayloadAction<{ isInspecting: boolean }>) {
      state.isInspecting = action.payload.isInspecting;
    },
    setAreUploadsSorted(state, action: PayloadAction<{ areUploadsSorted: boolean }>) {
      state.areUploadsSorted = action.payload.areUploadsSorted;
    },
    appendScanErrors(state, action: PayloadAction<{ uploadId: number; issues: Issue[] }>) {
      const caseState = state.cases[action.payload.uploadId];
      if (caseState) {
        caseState.errors = [...caseState.errors, ...action.payload.issues];
      }
    },
    appendScanWarnings(state, action: PayloadAction<{ uploadId: number; issues: Issue[] }>) {
      const caseState = state.cases[action.payload.uploadId];
      if (caseState) {
        caseState.warnings = [...caseState.warnings, ...action.payload.issues];
      }
    },
    appendScanDuplicates(state, action: PayloadAction<{ uploadId: number; issues: Issue[] }>) {
      const caseState = state.cases[action.payload.uploadId];
      if (caseState) {
        caseState.duplicates = [...caseState.duplicates, ...action.payload.issues];
      }
    },
    setScanIssues(
      state,
      action: PayloadAction<{
        uploadId: number;
        errors: Issue[];
        warnings: Issue[];
        duplicates: Issue[];
      }>
    ) {
      const caseState = state.cases[action.payload.uploadId];
      if (caseState) {
        caseState.errors = action.payload.errors;
        caseState.warnings = action.payload.warnings;
        caseState.duplicates = action.payload.duplicates;
      }
    },
    removeScan: (state, action: PayloadAction<{ uploadId: number; scanId: string }>) => {
      const { uploadId, scanId } = action.payload;
      const caseState = state.cases[uploadId];

      if (!caseState) {
        return;
      }

      caseState.scans = caseState.scans.filter((scan) => scan != scanId);

      // Delete the corresponding mapping entry
      delete state.scans[scanId];
      if (caseState.lowerScan === scanId) {
        caseState.lowerScan = undefined;
      }
      if (caseState.upperScan === scanId) {
        caseState.upperScan = undefined;
      }
    },
    removeCase: (state, action: PayloadAction<{ uploadId: number }>) => {
      const caseState = state.cases[action.payload.uploadId];
      if (!caseState) {
        return;
      }

      if (!caseState.isProcessed) {
        state.currentProcessingBatchSize -= 1;
      }
      state.numCases -= 1;
      delete state.cases[action.payload.uploadId];
    },
    addCase: (state, action: PayloadAction<{ uploadId: number; caseUploadState: CaseState }>) => {
      state.cases[action.payload.uploadId] = action.payload.caseUploadState;
      state.numCases += 1;
      if (!action.payload.caseUploadState.isProcessed) {
        state.currentProcessingBatchSize += 1;
      }
    },
    setScans: (
      state,
      action: PayloadAction<{ uploadId: number; scans: string[]; scanIndexMapping: ScanMapping; lowerScan?: string; upperScan?: string }>
    ) => {
      if (!state.cases[action.payload.uploadId]) {
        return;
      }
      state.cases[action.payload.uploadId].scans = action.payload.scans;
      state.scans = { ...state.scans, ...action.payload.scanIndexMapping };
      state.cases[action.payload.uploadId].upperScan = action.payload.upperScan;
      state.cases[action.payload.uploadId].lowerScan = action.payload.lowerScan;
    },
    setIsProcessed: (state, action: PayloadAction<{ uploadId: number; isProcessed: boolean }>) => {
      if (!state.cases[action.payload.uploadId]) {
        return;
      }
      state.cases[action.payload.uploadId].isProcessed = action.payload.isProcessed;
      if (action.payload.isProcessed && Object.values(state.cases).every((caseData?: CaseState) => caseData?.isProcessed)) {
        state.currentProcessingBatchSize = 0;
      }
    },
    clearUploadSliceData(state, _: PayloadAction<undefined>) {
      state.cases = [];
      state.scans = {};
      state.numCases = 0;
      state.currentProcessingBatchSize = 0;
    },
    setUpperScan(state, action: PayloadAction<{ uploadId: number; upperScan?: string }>) {
      if (!state.cases[action.payload.uploadId]) {
        return;
      }

      if (action.payload.upperScan !== undefined && !state.cases[action.payload.uploadId].scans.includes(action.payload.upperScan)) {
        return;
      }
      state.cases[action.payload.uploadId].upperScan = action.payload.upperScan;
    },
    setLowerScan(state, action: PayloadAction<{ uploadId: number; lowerScan?: string }>) {
      if (!state.cases[action.payload.uploadId]) {
        return;
      }

      if (action.payload.lowerScan !== undefined && !state.cases[action.payload.uploadId].scans.includes(action.payload.lowerScan)) {
        return;
      }
      state.cases[action.payload.uploadId].lowerScan = action.payload.lowerScan;
    },
    setScanLabel(state, action: PayloadAction<{ uploadId: number; scanId: string; label: ModelLabel | undefined }>) {
      if (!state.scans[action.payload.scanId]) {
        return;
      }

      state.scans[action.payload.scanId].label = action.payload.label;
    },
    setScanMeshEmpty(state, action: PayloadAction<{ uploadId: number; scanId: string; empty: boolean }>) {
      if (!state.scans[action.payload.scanId]) {
        return;
      }

      state.scans[action.payload.scanId].meshEmpty = action.payload.empty;
    },
    setScanMeshIsBased(state, action: PayloadAction<{ uploadId: number; scanId: string; based: boolean }>) {
      if (!state.scans[action.payload.scanId]) {
        return;
      }

      state.scans[action.payload.scanId].isBased = action.payload.based;
    },
    rangeSelectScans(state, action: PayloadAction<{ uploadId: number; scanId: string }>) {
      if (
        !state.scans[action.payload.scanId] ||
        !state.cases[action.payload.uploadId] ||
        !state.cases[action.payload.uploadId].scans.includes(action.payload.scanId)
      ) {
        return;
      }

      const caseScans = state.cases[action.payload.uploadId].scans;
      const selected = state.selected[action.payload.uploadId];
      if (selected === undefined || selected.length === 0) {
        state.selected[action.payload.uploadId] = [action.payload.scanId];
        return;
      }

      const lastSelected = selected.reverse().find((scanId) => caseScans.includes(scanId));
      if (lastSelected === undefined) {
        state.selected[action.payload.uploadId] = [...state.selected[action.payload.uploadId], action.payload.scanId];
        return;
      }
      const [a, b] = [caseScans.indexOf(lastSelected), caseScans.indexOf(action.payload.scanId)].sort((x, y) => x - y);
      const newSelectedScans = caseScans.slice(a, b + 1);

      state.selected[action.payload.uploadId] = Array.from(new Set([...state.selected[action.payload.uploadId], ...newSelectedScans]));
    },
    setSelectedScan(state, action: PayloadAction<{ uploadId: number; scanId: string }>) {
      if (
        !state.scans[action.payload.scanId] ||
        !state.cases[action.payload.uploadId] ||
        !state.cases[action.payload.uploadId].scans.includes(action.payload.scanId)
      ) {
        return;
      }

      state.selected = { [action.payload.uploadId]: [action.payload.scanId] };
    },
    addSelectedScan(state, action: PayloadAction<{ uploadId: number; scanId: string }>) {
      if (
        !state.scans[action.payload.scanId] ||
        !state.cases[action.payload.uploadId] ||
        !state.cases[action.payload.uploadId].scans.includes(action.payload.scanId)
      ) {
        return;
      }

      if (state.selected[action.payload.uploadId] === undefined) {
        state.selected[action.payload.uploadId] = [];
      }

      if (!state.selected[action.payload.uploadId].includes(action.payload.scanId)) {
        state.selected[action.payload.uploadId].push(action.payload.scanId);
      }
    },
    toggleSelectedScan(state, action: PayloadAction<{ uploadId: number; scanId: string }>) {
      if (
        !state.scans[action.payload.scanId] ||
        !state.cases[action.payload.uploadId] ||
        !state.cases[action.payload.uploadId].scans.includes(action.payload.scanId)
      ) {
        return;
      }

      if (state.selected[action.payload.uploadId] === undefined) {
        state.selected[action.payload.uploadId] = [];
      }

      if (!state.selected[action.payload.uploadId].includes(action.payload.scanId)) {
        state.selected[action.payload.uploadId].push(action.payload.scanId);
      } else {
        state.selected[action.payload.uploadId] = state.selected[action.payload.uploadId].filter(
          (scanId) => scanId != action.payload.scanId
        );
      }
    },
    clearSelectedScans(state, _action: PayloadAction<undefined>) {
      state.selected = {};
    },
    moveSelected(state, action: PayloadAction<{ removeFrom: { [uploadId: number]: string[] }; addTo: number }>) {
      if (!state.cases[action.payload.addTo]) {
        return;
      }

      let addScanIds: string[] = [];
      for (const [upload, scanIds] of Object.entries(action.payload.removeFrom)) {
        addScanIds = addScanIds.concat(scanIds);
        const uploadId = Number(upload);
        if (!state.cases[uploadId]) {
          continue;
        }

        state.cases[uploadId].scans = state.cases[uploadId].scans.filter((scanId) => !scanIds.includes(scanId));

        if (state.cases[uploadId].upperScan === undefined || scanIds.includes(state.cases[uploadId].upperScan)) {
          state.cases[uploadId].upperScan = state.cases[uploadId].scans.find((scanId) => state.scans[scanId].label === "upper");
        }
        if (state.cases[uploadId].lowerScan === undefined || scanIds.includes(state.cases[uploadId].lowerScan)) {
          state.cases[uploadId].lowerScan = state.cases[uploadId].scans.find((scanId) => state.scans[scanId].label === "lower");
        }
      }

      state.cases[action.payload.addTo].scans = [
        ...state.cases[action.payload.addTo].scans,
        ...addScanIds.filter((scanId) => !state.cases[action.payload.addTo].scans.includes(scanId))
      ];

      if (state.cases[action.payload.addTo].upperScan === undefined) {
        state.cases[action.payload.addTo].upperScan = state.cases[action.payload.addTo].scans.find(
          (scanId) => state.scans[scanId].label === "upper"
        );
      }
      if (state.cases[action.payload.addTo].lowerScan === undefined) {
        state.cases[action.payload.addTo].lowerScan = state.cases[action.payload.addTo].scans.find(
          (scanId) => state.scans[scanId].label === "lower"
        );
      }
    },
    setDraggingOver(state, action: PayloadAction<{ uploadId: number; isOver: boolean; overScan?: boolean }>) {
      if (action.payload.isOver) {
        if (state.draggingOver?.id === action.payload.uploadId) {
          state.draggingOver.eventSeen++;
          state.draggingOver.overScan = action.payload.overScan ?? state.draggingOver.overScan;
        } else {
          state.draggingOver = {
            id: action.payload.uploadId,
            eventSeen: 1,
            overScan: action.payload.overScan ?? false
          };
        }
      } else if (state.draggingOver?.id === action.payload.uploadId) {
        state.draggingOver.eventSeen--;
        if (state.draggingOver.eventSeen < 1) {
          state.draggingOver = undefined;
        }
      }
    },
    setIsUploading(state, action: PayloadAction<boolean>) {
      state.isUploading = action.payload;
    },
    setNoScansDetectedError(state, action: PayloadAction<boolean>) {
      state.noScansDetectedError = action.payload;
    },
    setEmptyFolderError(state, action: PayloadAction<boolean>) {
      state.emptyFolderError = action.payload;
    }
  },
  selectors: {
    selectCaseUpload: (state, uploadId: number) => state.cases[uploadId],
    selectAreCaseUploadThumbnailsAvailable: (state, uploadId: number): boolean => state.cases[uploadId]?.areThumbnailsAvailable === true,
    selectAreCaseUploadThumbnailsLoading: (state, uploadId: number): boolean => state.cases[uploadId]?.areThumbnailsLoading === true,
    selectHighestUploadId: (state): number => state.highestUploadId,
    selectCaseUploads: (state) => {
      return state.cases;
    },
    selectNumCaseUploads: (state) => state.numCases,
    selectCaseScans: (state, uploadId: number) => state.cases[uploadId].scans,
    selectScan: (state, scanId: string): ScanMappingValue | undefined => state.scans[scanId],
    selectScans: (state) => state.scans,
    selectScanFilename: (state, scanId: string) => {
      return state.scans[scanId]?.filename;
    },
    selectIsInspecting: (state) => state.isInspecting,
    selectCaseName: (state, caseId: number): string => {
      const caseData = state.cases[caseId];
      return caseData ? caseData.name : "Unknown Case";
    },
    selectCaseIssues: (state, uploadId: number): [Issue[], Issue[]] | undefined => {
      const caseState = state.cases[uploadId];
      if (!caseState) {
        return;
      }
      return [caseState.errors, caseState.warnings];
    },
    selectCaseWarnings: (state, uploadId: number): Issue[] | undefined => state.cases[uploadId]?.warnings,
    selectCaseErrors: (state, uploadId: number): Issue[] | undefined => state.cases[uploadId]?.errors,
    selectCaseDuplicates: (state, uploadId: number): Issue[] | undefined => state.cases[uploadId]?.duplicates,
    selectNumWarnings: (state): number =>
      Object.values(state.cases).filter(
        (caseData?: CaseState) =>
          caseData?.warnings?.find(
            (issue) => issue.scanId === caseData.lowerScan || issue.scanId === caseData.upperScan || issue.scanId === null
          ) !== undefined
      ).length,
    selectNumErrors: (state): number =>
      Object.values(state.cases).filter(
        (caseData?: CaseState) =>
          caseData?.errors?.find(
            (issue) => issue.scanId === caseData.lowerScan || issue.scanId === caseData.upperScan || issue.scanId === null
          ) !== undefined
      ).length,
    selecteNumScans: (state, uploadId: number): number => state.cases[uploadId].scans.length,
    selectAreUploadsSorted: (state): boolean => state.areUploadsSorted,
    selectIsProcessing: (state): boolean => !Object.values(state.cases).every((dentalCase) => dentalCase == null || dentalCase.isProcessed),
    selectNumProcessed: (state): number =>
      Object.values(state.cases).filter((dentalCase) => dentalCase != null && dentalCase.isProcessed).length,
    selectNumUnprocessed: (state): number =>
      Object.values(state.cases).filter((dentalCase) => dentalCase != null && !dentalCase.isProcessed).length,
    selectCurrentProcessingBatchSize: (state): number => state.currentProcessingBatchSize,
    selectLowerScan: (state, uploadId: number) => state.cases[uploadId]?.lowerScan,
    selectUpperScan: (state, uploadId: number) => state.cases[uploadId]?.upperScan,
    selectAreThumbnailsLoading: (state) =>
      Object.values(state.cases).some((caseData?: CaseState | undefined) => caseData != null && caseData.areThumbnailsLoading),
    selectIsScanSelected: (state, uploadId: number, scanId: string) => state.selected?.[uploadId]?.includes(scanId) ?? false,
    selectSelectedScans: (state) => state.selected,
    selectDraggingOver: (state) => state.draggingOver?.id,
    selectDraggingOverScan: (state) => state.draggingOver?.overScan ?? false,
    selectIsUploading: (state) => state.isUploading,
    selectNoScansDetectedError: (state) => state.noScansDetectedError,
    selectEmptyFolderError: (state) => state.emptyFolderError
  },
  extraReducers: (builder) => {
    builder.addCase(uploadNewCase.fulfilled, (state) => {
      state.areUploadsSorted = false;
      // Errors, warnings and thumbnails are handled when this is complete
    });
    builder.addCase(clearCaseUploads.fulfilled, (state) => {
      state.areUploadsSorted = false;
      state.cases = [];
      state.numCases = 0;
      state.currentProcessingBatchSize = 0;
    });
    builder.addCase(deleteCaseUpload.fulfilled, (state, action) => {
      const uploadId = action.payload;
      const caseToDelete = state.cases[uploadId];
      if (caseToDelete) {
        for (const scanId of caseToDelete.scans) {
          delete state.scans[scanId];
        }
        delete state.cases[uploadId];
        state.numCases -= 1;
        if (!caseToDelete.isProcessed) {
          state.currentProcessingBatchSize--;
        }
      }
    });
    builder.addCase(generateThumbnails.fulfilled, (state, action) => {
      if (state.cases[action.payload]) {
        state.cases[action.payload].areThumbnailsAvailable = true;
      }
    });
    builder.addCase(detectIssues.fulfilled, (state, action) => {
      state.cases[action.payload.uploadId].warnings = action.payload.warnings;
      state.cases[action.payload.uploadId].errors = action.payload.errors;
    });
  }
});

const selectCaseIssues = createSelector(
  [
    (state: RootState, uploadId: number) => state.cases.cases[uploadId]?.errors,
    (state: RootState, uploadId: number) => state.cases.cases[uploadId]?.warnings
  ],
  (errors, warnings) => {
    if (!errors || !warnings) {
      return;
    }
    return [errors, warnings];
  }
);

const selectInspecting = createSelector(
  [(state: RootState) => state.cases.inspectingCaseIndex, (state: RootState) => state.cases.inspectingScanId],
  (inspectingCaseIndex, inspectingScanIndex) => ({
    caseId: inspectingCaseIndex,
    scanId: inspectingScanIndex
  })
);

interface selectScanArgs {
  state: RootState;
  uploadId: number;
  scanId: string;
}
const selectScanErrors = createSelector(
  [({ state, uploadId }: selectScanArgs) => state.cases.cases[uploadId]?.errors, ({ scanId: scanIndex }: selectScanArgs) => scanIndex],
  (errors, scanId) => {
    if (!errors) {
      return [];
    }

    return errors.filter((e: Issue) => e.scanId === scanId).sort((a: Issue, b: Issue) => b.severity - a.severity);
  }
);

const selectScanWarnings = createSelector(
  [({ state, uploadId }: selectScanArgs) => state.cases.cases[uploadId].warnings, ({ scanId: scanIndex }: selectScanArgs) => scanIndex],
  (warnings, scanId) => {
    if (!warnings) {
      return [];
    }
    return warnings.filter((w: Issue) => w.scanId === scanId).sort((a: Issue, b: Issue) => b.severity - a.severity);
  }
);

const selectScanDuplicates = createSelector(
  [({ state, uploadId }: selectScanArgs) => state.cases.cases[uploadId].duplicates, ({ scanId: scanIndex }: selectScanArgs) => scanIndex],
  (duplicates, scanIndex) => {
    if (!duplicates) {
      return [];
    }
    return duplicates.filter((w: Issue) => w.scanId === scanIndex).sort((a: Issue, b: Issue) => b.severity - a.severity);
  }
);

interface NewCase {
  name: string;
  scans: ScanObject[];
}

const uploadNewCase = createAsyncThunk("cases/uploadNewCase", async (newCase: NewCase, { getState, dispatch }) => {
  // add case
  const highestUploadId = selectHighestUploadId(getState() as RootState);
  const uploadId = highestUploadId + 1;
  const caseUploadState: CaseState = {
    uploadId,
    name: newCase.name || "",
    expanded: true,
    areThumbnailsAvailable: false,
    areThumbnailsLoading: false,
    scans: [],
    errors: [],
    warnings: [],
    duplicates: [],
    isProcessed: false
  };
  dispatch(setHighestUploadId(uploadId));
  dispatch(casesSlice.actions.addCase({ uploadId, caseUploadState: caseUploadState }));

  try {
    // Sort the scans chronologically, with the most recent upper and lower to the top
    const sortedScans = await Promise.all(sortScans(newCase.scans).map(async (scan) => ({ data: await scan.scan.arrayBuffer(), ...scan })));

    const caseDataIndexedDB: NewUploadObjectIndexedDB = {
      scans: sortedScans.map((scan) => ({ data: scan.data, type: scan.type, label: scan.label })),
      timestamp: Date.now(),
      uploadId
    };

    try {
      await invalidateIndexedDBData();
    } catch (_) {
      // Ignore error. There should usually be space available for uploads.
    }

    const scanIds = await addNewUploadToIndexedDB(caseDataIndexedDB);

    dispatch(
      casesSlice.actions.setScans({
        uploadId,
        scans: scanIds,
        scanIndexMapping: sortedScans.reduce<ScanMapping>(
          (acc, scan, index) => ({
            ...acc,
            [scanIds[index]]: {
              label: scan.label,
              filename: scan.filename,
              lastModified: scan.lastModified
            }
          }),
          {} as ScanMapping
        ),
        upperScan: scanIds.find((_, index) => sortedScans[index].label === "upper"),
        lowerScan: scanIds.find((_, index) => sortedScans[index].label === "lower")
      })
    );
    return { uploadId };
  } catch (error) {
    dispatch(casesSlice.actions.removeCase({ uploadId }));
    throw error; // redux will handle rejecting with a serialised version of this error
  }
});

const addEmptyCase = createAsyncThunk(
  "cases/newEmptyCase",
  async ({ name, onNewEmptyCaseCreated }: { name: string; onNewEmptyCaseCreated?: () => void }, { dispatch, getState }) => {
    const highestUploadId = selectHighestUploadId(getState() as RootState);
    const uploadId = highestUploadId + 1;
    const caseUploadState: CaseState = {
      uploadId,
      name: name,
      expanded: true,
      areThumbnailsAvailable: false,
      areThumbnailsLoading: false,
      scans: [],
      errors: [],
      warnings: [],
      duplicates: [],
      isProcessed: true
    };
    dispatch(setHighestUploadId(uploadId));
    dispatch(casesSlice.actions.addCase({ uploadId, caseUploadState: caseUploadState }));

    try {
      await addNewUploadToIndexedDB({
        scans: [],
        timestamp: Date.now(),
        uploadId
      });
    } catch (error) {
      dispatch(casesSlice.actions.removeCase({ uploadId }));
      throw error; // redux will handle rejecting with a serialised version of this error
    }

    if (onNewEmptyCaseCreated) {
      onNewEmptyCaseCreated();
    }

    return uploadId;
  }
);

// Used to clear the uploads cache once the cases are uploaded
const clearCaseUploads = createAsyncThunk("cases/clearCaseUploads", async (_) => {
  await clearUploadsStore();
});

// Delete a case
const deleteCaseUpload = createAsyncThunk("cases/deleteCaseUpload", async (uploadId: number) => {
  await deleteUploadFromStore(uploadId);
  return uploadId;
});

const setScanLabel = createAsyncThunk(
  "cases/setScanLabel",
  async ({ uploadId, scanId, label }: { uploadId: number; scanId: string; label: ModelLabel }, { dispatch, getState }) => {
    await classifyScanInIndexedDB(scanId, label);

    dispatch(casesSlice.actions.setScanLabel({ uploadId, scanId, label }));

    let state = getState() as RootState;
    const selectedLower = selectCaseUpload(state, uploadId).lowerScan;
    const selectedUpper = selectCaseUpload(state, uploadId).upperScan;

    // Deselect invalid selections
    if (selectedLower === scanId && (label === "upper" || label === undefined)) {
      dispatch(casesSlice.actions.setLowerScan({ uploadId, lowerScan: undefined }));
    }
    if (selectedUpper === scanId && (label === "lower" || label === undefined)) {
      dispatch(casesSlice.actions.setUpperScan({ uploadId, upperScan: undefined }));
    }

    // Select changed scan
    if (label === "lower") {
      dispatch(casesSlice.actions.setLowerScan({ uploadId, lowerScan: scanId }));
    } else if (label === "upper") {
      dispatch(casesSlice.actions.setUpperScan({ uploadId, upperScan: scanId }));
    }

    // Refresh state after above changes
    state = getState() as RootState;
    const caseState = selectCaseUpload(state, uploadId);

    // Auto assign undefined lower and upper selections
    if (caseState.upperScan === undefined) {
      const newUpper = caseState.scans.find((scanIndex) => state.cases.scans[scanIndex].label === "upper");
      dispatch(casesSlice.actions.setUpperScan({ uploadId, upperScan: newUpper }));
    }
    if (caseState.lowerScan === undefined) {
      const newLower = caseState.scans.find((scanIndex) => state.cases.scans[scanIndex].label === "lower");
      dispatch(casesSlice.actions.setLowerScan({ uploadId, lowerScan: newLower }));
    }

    await dispatch(detectIssues(uploadId));
    await dispatch(generateThumbnails(uploadId));
  }
);

const deleteScanUpload = createAsyncThunk<{ uploadId: number; scanId: string }, [number, string], { rejectValue: string }>(
  "cases/deleteScanUpload",
  async ([uploadId, scanId], { dispatch, getState }) => {
    // delete from indexed
    await removeAndAddScansFromIndexedDB({ [uploadId]: [scanId] });

    dispatch(casesSlice.actions.removeScan({ uploadId, scanId }));

    const state = (getState() as RootState).cases;
    const caseState = state.cases[uploadId];

    let selectionChanged = false;
    if (caseState.upperScan === undefined) {
      const newUpper = caseState.scans.find((scanId) => state.scans[scanId].label === "upper");
      dispatch(casesSlice.actions.setUpperScan({ uploadId, upperScan: newUpper }));
      selectionChanged = true;
    }
    if (caseState.lowerScan === undefined) {
      const newLower = caseState.scans.find((scanId) => state.scans[scanId].label === "lower");
      dispatch(casesSlice.actions.setLowerScan({ uploadId, lowerScan: newLower }));
      selectionChanged = true;
    }

    await dispatch(detectIssues(uploadId));
    if (selectionChanged) {
      await dispatch(generateThumbnails(uploadId));
    }

    return { uploadId, scanId: scanId };
  }
);

const generateThumbnails = createAsyncThunk("cases/generateThumbnails", async (uploadId: number, { getState, dispatch }) => {
  const scans = await getUploadScansFromIndexedDB(uploadId);

  const caseState = casesSlice.selectors.selectCaseUpload(getState() as RootState, uploadId);
  dispatch(casesSlice.actions.setCaseUploadThumbnailsAreLoading({ uploadId, loading: true }));

  // Generate scan thumbnails
  await Promise.all(
    scans.map(async (scan) => {
      const thumbnail = await generateModelThumbnail([scan]);
      await setThumbnailFromIndexedDB(scan.id, thumbnail);
    })
  );

  // Generate combined thumbnail
  const lastUpper = caseState.upperScan != undefined ? scans.find((scan) => scan.id === caseState.upperScan) : undefined;
  const lastLower = caseState.lowerScan != undefined ? scans.find((scan) => scan.id === caseState.lowerScan) : undefined;
  const combined = [lastUpper, lastLower].filter((scan) => scan !== undefined);

  if (combined.length !== 0) {
    const combinedThumbnail = await generateModelThumbnail(combined);
    combinedThumbnail.label = "combined";
    await setCombinedThumbnailFromIndexedDB(uploadId, combinedThumbnail);
  } else {
    // Set a blank thumbnail if there are no valid scans
    await setCombinedThumbnailFromIndexedDB(uploadId, {
      data: null,
      label: "combined",
      type: "png"
    });
  }

  dispatch(casesSlice.actions.setCaseUploadThumbnailsAreLoading({ uploadId, loading: false }));
  dispatch(casesSlice.actions.setAreCaseUploadThumbnailsAvailable({ uploadId, areAvailable: true }));

  return uploadId;
});

const moveSelectedScans = createAsyncThunk(
  "cases/moveSelectedScans",
  async (args: { uploadId: number } | { newCaseName: string }, { dispatch, getState }) => {
    const selected = (getState() as RootState).cases.selected;

    let uploadId;
    if ("newCaseName" in args) {
      uploadId = await dispatch(addEmptyCase({ name: args.newCaseName })).unwrap();
    } else {
      uploadId = args.uploadId;
    }

    dispatch(casesSlice.actions.moveSelected({ removeFrom: selected, addTo: uploadId }));
    await removeAndAddScansFromIndexedDB(selected, uploadId);
    dispatch(clearSelectedScans());

    const cases = (getState() as RootState).cases.cases;
    await Promise.allSettled([
      dispatch(generateThumbnails(uploadId)),
      dispatch(detectIssues(uploadId)),
      ...Object.keys(selected)
        .map((id) => {
          const uploadId = Number(id);

          if (cases[uploadId]?.scans?.length === 0) {
            return [dispatch(deleteCaseUpload(uploadId))];
          }

          return [dispatch(generateThumbnails(Number(id))), dispatch(detectIssues(Number(id)))];
        })
        .flat()
    ]);
  }
);

interface IssueDetectionOutput {
  uploadId: number;
  errors: Issue[];
  warnings: Issue[];
  duplicates: Issue[];
}

const detectIssues = createAsyncThunk<IssueDetectionOutput, number>("cases/detectIssues", async (uploadId, { getState, dispatch }) => {
  const scans = await getUploadScansFromIndexedDB(uploadId);

  const state = getState() as RootState;
  const allScans = state.cases.scans;
  const caseData = selectCaseUpload(state, uploadId);
  const errors: Issue[] = [];
  const warnings: Issue[] = [];
  const duplicates: Issue[] = [];

  // count duplicate upper or lower scans and return their indices if duplicates exists
  const upperScans = scans.filter((scan) => scan.label === "upper");
  const lowerScans = scans.filter((scan) => scan.label === "lower");

  const numUpperScans = upperScans?.length ?? 0;
  const numLowerScans = lowerScans?.length ?? 0;

  if (caseData.lowerScan === undefined && caseData.upperScan === undefined) {
    const issue: Issue = {
      scanId: null,
      code: "missingUpperAndLower",
      severity: 1
    };
    errors.push(issue);
  } else if (caseData.lowerScan === undefined || caseData.upperScan === undefined) {
    const issue: Issue = {
      scanId: null,
      code: "missingUpperOrLower",
      severity: 1
    };
    warnings.push(issue);
  }

  if (numUpperScans > 1) {
    for (const upperScan of upperScans ?? []) {
      const issue: Issue = {
        scanId: upperScan.id,
        code: "multipleUpperOrLower",
        severity: 1
      };
      duplicates.push(issue);
    }
  }
  if (numLowerScans > 1) {
    for (const lowerScan of lowerScans ?? []) {
      const issue: Issue = {
        scanId: lowerScan.id,
        code: "multipleUpperOrLower",
        severity: 1
      };
      duplicates.push(issue);
    }
  }

  const scanGeometry = await Promise.all(scans?.map(async (scan) => await parseDataToGeometry([scan])) ?? []);

  for (let i = 0; i < scanGeometry.length; i++) {
    const scan = scans[i];
    const geometry = scanGeometry[i];

    let isEmpty;
    if (allScans[scan.id].meshEmpty !== undefined) {
      isEmpty = allScans[scan.id].meshEmpty;
    } else {
      isEmpty = geometry === undefined || geometry.getAttribute("position").count === 0;
      dispatch(casesSlice.actions.setScanMeshEmpty({ uploadId, scanId: scan.id, empty: isEmpty }));
    }

    if (isEmpty) {
      const id = scan.id;
      const issue: Issue = {
        scanId: id,
        code: "emptyMesh",
        severity: 1
      };
      if (caseData.upperScan === id || caseData.lowerScan === id) {
        errors.push(issue);
      } else {
        warnings.push(issue);
      }
    }

    let isBased;
    if (allScans[scan.id].isBased !== undefined) {
      isBased = allScans[scan.id].isBased;
    } else {
      isBased = geometry !== undefined && isBasedModel(geometry);
      dispatch(casesSlice.actions.setScanMeshIsBased({ uploadId, scanId: scan.id, based: isBased }));
    }

    if (isBased) {
      const id = scan.id;
      const issue: Issue = {
        scanId: id,
        code: "basedModel",
        severity: 1
      };
      if (caseData.upperScan === id || caseData.lowerScan === id) {
        errors.push(issue);
      } else {
        warnings.push(issue);
      }
    }
  }

  dispatch(
    setScanIssues({
      uploadId,
      errors,
      warnings,
      duplicates
    })
  );
  return { uploadId, errors, warnings, duplicates };
});

startCaseUploadListening({
  actionCreator: uploadNewCase.fulfilled,
  effect: async (action, listenerApi) => {
    // Dispatch generateThumbnails thunk when uploadNewCase is fulfilled
    await listenerApi.dispatch(detectIssues(action.payload.uploadId));
    await listenerApi.dispatch(generateThumbnails(action.payload.uploadId));

    listenerApi.dispatch(
      casesSlice.actions.setIsProcessed({
        uploadId: action.payload.uploadId,
        isProcessed: true
      })
    );
  }
});

export interface ScanMappingValue {
  label?: ModelLabel;
  filename: string;
  lastModified: number;
  meshEmpty?: boolean;
  isBased?: boolean;
}

export interface ScanMapping {
  [key: string]: ScanMappingValue;
}

export interface CasesMapping {
  [key: number]: CaseState;
}

export interface CaseState {
  uploadId: number;
  name: string;
  expanded: boolean;
  areThumbnailsAvailable: boolean;
  areThumbnailsLoading: boolean;
  lowerScan?: string;
  upperScan?: string;
  scans: string[];
  errors: Issue[]; // sorted descending by severity
  warnings: Issue[]; // sorted descending by severity
  duplicates: Issue[];
  isProcessed: boolean;
}

export const {
  setHighestUploadId,
  setCaseName,
  setExpandCaseUpload,
  setInspecting,
  setIsInspecting,
  setAreUploadsSorted,
  appendScanErrors,
  appendScanWarnings,
  appendScanDuplicates,
  setScanIssues,
  clearUploadSliceData,
  addSelectedScan,
  toggleSelectedScan,
  clearSelectedScans,
  setSelectedScan,
  rangeSelectScans,
  setDraggingOver,
  setEmptyFolderError,
  setNoScansDetectedError,
  setIsUploading
} = casesSlice.actions;
export const {
  selectHighestUploadId,
  selectCaseUploads,
  selectCaseUpload,
  selectNumCaseUploads,
  selectAreCaseUploadThumbnailsAvailable,
  selectAreCaseUploadThumbnailsLoading,
  selectAreThumbnailsLoading,
  selectIsInspecting,
  selectCaseName,
  selectAreUploadsSorted,
  selectCaseErrors,
  selectCaseWarnings,
  selectNumErrors,
  selectCaseDuplicates,
  selectNumWarnings,
  selectScanFilename,
  selecteNumScans,
  selectIsProcessing,
  selectNumProcessed,
  selectNumUnprocessed,
  selectCurrentProcessingBatchSize,
  selectUpperScan,
  selectLowerScan,
  selectScan,
  selectScans,
  selectIsScanSelected,
  selectSelectedScans,
  selectDraggingOver,
  selectDraggingOverScan,
  selectEmptyFolderError,
  selectNoScansDetectedError,
  selectIsUploading
} = casesSlice.selectors;
export default casesSlice.reducer;
export {
  addEmptyCase,
  caseUploadsListener,
  clearCaseUploads,
  deleteCaseUpload,
  deleteScanUpload,
  detectIssues,
  generateThumbnails,
  moveSelectedScans,
  selectCaseIssues,
  selectInspecting,
  selectScanDuplicates,
  selectScanErrors,
  selectScanWarnings,
  setScanLabel,
  sortScans,
  uploadNewCase
};
